import { useQueryClient } from '@tanstack/react-query'
import { FileText, UploadCloud, X } from 'lucide-react'
import { useEffect, useRef, useState, type ChangeEvent, type DragEvent } from 'react'
import { useTranslation } from 'react-i18next'

import { ApiError } from '@/api/problemDetails'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  SpinnerIcon,
  type BadgeProps,
} from '@/components/ui'
import { cn } from '@/lib/utils'

import { uploadDocument } from '../api'
import { knowledgeDocumentsQueryKey, useImportStatus } from '../queries'
import type { DocumentClassification } from '../types'

type BadgeVariant = NonNullable<BadgeProps['variant']>

/** Formatos aceitos pelo backend (allow-list por extensao). Imagens NAO sao suportadas nesta fase. */
const ACCEPTED_EXTENSIONS: readonly string[] = ['.txt', '.md', '.pdf', '.docx', '.xlsx']
const ACCEPT_ATTR = ACCEPTED_EXTENSIONS.join(',')
/** Espelha `KnowledgeIngestionService.MaxDocumentSizeBytes` (10 MiB). O servidor e a autoridade; isto e feedback rapido. */
const MAX_SIZE_BYTES = 10 * 1024 * 1024

type RejectionReason = 'unsupported_type' | 'file_too_large' | 'empty_file'

/**
 * Um arquivo em envio. `importId` fica `null` ate o POST resolver (fase "enviando"); `uploadError` fica `null`
 * ate/salvo o POST falhar. O upload e disparado no HANDLER (nao em efeito), entao o resultado e gravado aqui
 * no estado do painel — sem depender do observer de uma mutation, que o StrictMode desconectaria.
 */
interface AcceptedUpload {
  readonly id: string
  readonly file: File
  readonly importId: string | null
  readonly uploadError: unknown
}

interface RejectedUpload {
  readonly id: string
  readonly name: string
  readonly reason: RejectionReason
}

function fileExtension(name: string): string {
  const dot = name.lastIndexOf('.')
  return dot >= 0 ? name.slice(dot).toLowerCase() : ''
}

/** Validacao no cliente (feedback imediato). O servidor revalida por magic bytes + tamanho e continua a autoridade. */
function validateFile(file: File): RejectionReason | null {
  if (!ACCEPTED_EXTENSIONS.includes(fileExtension(file.name))) {
    return 'unsupported_type'
  }
  if (file.size === 0) {
    return 'empty_file'
  }
  if (file.size > MAX_SIZE_BYTES) {
    return 'file_too_large'
  }
  return null
}

type RejectionReasonKey =
  | 'knowledge.upload.rejected.unsupportedType'
  | 'knowledge.upload.rejected.fileTooLarge'
  | 'knowledge.upload.rejected.emptyFile'

/** Chaves de mensagem de erro (upload + processamento), em vocabulario de produto. */
type UploadErrorKey =
  | 'knowledge.upload.errors.unsupportedType'
  | 'knowledge.upload.errors.fileTooLarge'
  | 'knowledge.upload.errors.emptyFile'
  | 'knowledge.upload.errors.unreadable'
  | 'knowledge.upload.errors.contentTooLarge'
  | 'knowledge.upload.errors.aiNotConfigured'
  | 'knowledge.upload.errors.processingFailed'
  | 'knowledge.upload.errors.processingTimeout'
  | 'knowledge.upload.errors.uploadTimeout'
  | 'knowledge.upload.errors.statusUnavailable'
  | 'knowledge.upload.errors.generic'

type PhaseLabelKey =
  | 'knowledge.upload.status.uploading'
  | 'knowledge.upload.status.processing'
  | 'knowledge.upload.status.ready'
  | 'knowledge.upload.status.error'

function rejectionReasonKey(reason: RejectionReason): RejectionReasonKey {
  switch (reason) {
    case 'file_too_large':
      return 'knowledge.upload.rejected.fileTooLarge'
    case 'empty_file':
      return 'knowledge.upload.rejected.emptyFile'
    default:
      return 'knowledge.upload.rejected.unsupportedType'
  }
}

/** Traduz o erro de upload (400/413/415) para uma mensagem de produto — nunca termos de infraestrutura. */
function uploadErrorKey(error: unknown): UploadErrorKey {
  if (!(error instanceof ApiError)) {
    return 'knowledge.upload.errors.generic'
  }
  const { status, errorCode } = error.problem
  if (status === 413) {
    return 'knowledge.upload.errors.fileTooLarge'
  }
  if (status === 415) {
    return 'knowledge.upload.errors.unsupportedType'
  }
  switch (errorCode) {
    case 'file_too_large':
      return 'knowledge.upload.errors.fileTooLarge'
    case 'unsupported_or_mismatched_file':
    case 'unsupported_media_type':
      return 'knowledge.upload.errors.unsupportedType'
    case 'empty_file':
    case 'no_file':
      return 'knowledge.upload.errors.emptyFile'
    case 'upload_timeout':
    case 'network_error':
      return 'knowledge.upload.errors.uploadTimeout'
    default:
      return 'knowledge.upload.errors.generic'
  }
}

/** Traduz a falha terminal do processamento (`DocumentImportResponse.lastErrorCode`) em vocabulario de produto. */
function importFailedKey(lastErrorCode: string | null): UploadErrorKey {
  switch (lastErrorCode) {
    case 'ai_not_configured':
      return 'knowledge.upload.errors.aiNotConfigured'
    case 'document_unreadable':
    case 'unsupported_format':
    case 'no_extractable_text':
      return 'knowledge.upload.errors.unreadable'
    case 'document_too_large':
      // O arquivo e legivel — so tem conteudo grande demais (ex.: planilha que infla). Mensagem de tamanho, nao "ilegivel".
      return 'knowledge.upload.errors.contentTooLarge'
    case 'extraction_timeout':
      // O documento e legivel — so excedeu o tempo de processamento. Mensagem propria (nunca "ilegivel").
      return 'knowledge.upload.errors.processingTimeout'
    default:
      return 'knowledge.upload.errors.processingFailed'
  }
}

type UploadPhase = 'uploading' | 'processing' | 'ready' | 'error'

function phaseBadgeVariant(phase: UploadPhase): BadgeVariant {
  switch (phase) {
    case 'ready':
      return 'success'
    case 'error':
      return 'danger'
    case 'processing':
      return 'info'
    default:
      return 'neutral'
  }
}

function phaseLabelKey(phase: UploadPhase): PhaseLabelKey {
  switch (phase) {
    case 'ready':
      return 'knowledge.upload.status.ready'
    case 'error':
      return 'knowledge.upload.status.error'
    case 'processing':
      return 'knowledge.upload.status.processing'
    default:
      return 'knowledge.upload.status.uploading'
  }
}

interface UploadItemProps {
  readonly tenantId: string
  readonly upload: AcceptedUpload
  readonly onDismiss: () => void
}

/**
 * Uma linha por arquivo. NAO dispara o upload (isso acontece no handler do painel, StrictMode-safe): apenas
 * reflete o estado recebido — enviando (sem importId) -> processando (poll do status) -> pronto/erro. Ao ficar
 * pronto, invalida a lista de documentos para o arquivo aparecer entre os que o agente ja aprendeu.
 */
function UploadItem({ tenantId, upload, onDismiss }: UploadItemProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { file, importId, uploadError } = upload

  const statusQuery = useImportStatus(tenantId, importId ?? undefined)
  const effectiveStatus = statusQuery.data?.status

  const isReady = effectiveStatus === 'Ready'
  useEffect(() => {
    if (isReady) {
      void queryClient.invalidateQueries({ queryKey: knowledgeDocumentsQueryKey(tenantId) })
    }
  }, [isReady, queryClient, tenantId])

  let phase: UploadPhase
  let message: string | null = null
  if (uploadError !== null) {
    phase = 'error'
    message = t(uploadErrorKey(uploadError))
  } else if (importId === null) {
    phase = 'uploading'
  } else if (effectiveStatus === 'Ready') {
    phase = 'ready'
  } else if (effectiveStatus === 'Failed') {
    phase = 'error'
    message = t(importFailedKey(statusQuery.data?.lastErrorCode ?? null))
  } else if (statusQuery.isError) {
    phase = 'error'
    message = t('knowledge.upload.errors.statusUnavailable')
  } else {
    phase = 'processing'
  }

  const inProgress = phase === 'uploading' || phase === 'processing'

  return (
    <li className="flex flex-col gap-1 rounded-md border border-border-default px-3 py-2">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <FileText aria-hidden="true" className="h-4 w-4 shrink-0 text-foreground-muted" />
          <span className="truncate text-sm text-foreground-primary" title={file.name}>
            {file.name}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {inProgress ? <SpinnerIcon size="sm" className="text-action-primary" /> : null}
          <Badge variant={phaseBadgeVariant(phase)}>{t(phaseLabelKey(phase))}</Badge>
          <button
            type="button"
            onClick={onDismiss}
            aria-label={t('knowledge.upload.item.dismiss', { name: file.name })}
            className="rounded text-foreground-muted transition-colors hover:text-foreground-primary"
          >
            <X aria-hidden="true" className="h-4 w-4" />
          </button>
        </div>
      </div>
      {message !== null ? <p className="text-xs text-status-danger">{message}</p> : null}
    </li>
  )
}

/**
 * "Envie documentos" (EPIC Importacao de documentos). Complementa o "Ensine seu agente" (site):
 * o agente aprende com o site E com os arquivos enviados aqui. Drag-and-drop nativo + selecao por
 * botao (acessivel via teclado); valida tipo/tamanho no cliente antes de enviar e mostra o progresso
 * de cada arquivo. Um request por arquivo. Vocabulario neutro — nunca expoe embeddings/chunks/blob/RAG.
 *
 * O upload e disparado no HANDLER (addFiles), nao num efeito por item: handlers nao sofrem o double-invoke
 * do StrictMode, entao cada arquivo e enviado exatamente uma vez e o resultado (importId/erro) e gravado no
 * estado. O padrao anterior (mutation disparada em useEffect com guarda de ref) deixava o item preso em
 * "Enviando" no StrictMode, porque o observer da mutation era recriado sem o resultado.
 */
export function UploadPanel({ tenantId }: { readonly tenantId: string }) {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const nextId = useRef(0)
  const [uploads, setUploads] = useState<readonly AcceptedUpload[]>([])
  const [rejections, setRejections] = useState<readonly RejectedUpload[]>([])
  const [isDragging, setIsDragging] = useState(false)
  // Visibilidade escolhida para a proxima selecao: desmarcado = visivel ao cliente (Public); marcado = uso interno.
  const [isInternal, setIsInternal] = useState(false)

  // Envia UM arquivo e grava o resultado no item correspondente (por id). O updater funcional sempre opera no
  // estado mais recente, entao e seguro mesmo que o item so tenha sido inserido logo antes desta chamada.
  const startUpload = (id: string, file: File, classification: DocumentClassification) => {
    void uploadDocument(tenantId, file, classification).then(
      (result) => {
        setUploads((current) =>
          current.map((item) => (item.id === id ? { ...item, importId: result.id } : item)),
        )
      },
      (error: unknown) => {
        setUploads((current) =>
          current.map((item) => (item.id === id ? { ...item, uploadError: error } : item)),
        )
      },
    )
  }

  const addFiles = (fileList: FileList | null) => {
    if (fileList === null || fileList.length === 0) {
      return
    }
    const accepted: AcceptedUpload[] = []
    const rejected: RejectedUpload[] = []
    for (const file of Array.from(fileList)) {
      nextId.current += 1
      const id = `upload-${String(nextId.current)}`
      const reason = validateFile(file)
      if (reason === null) {
        accepted.push({ id, file, importId: null, uploadError: null })
      } else {
        rejected.push({ id, name: file.name, reason })
      }
    }
    if (accepted.length > 0) {
      // Congela a visibilidade escolhida NO MOMENTO desta selecao (o toggle pode mudar depois, sem afetar estes arquivos).
      const classification: DocumentClassification = isInternal ? 'Internal' : 'Public'
      setUploads((current) => [...current, ...accepted])
      // Dispara o envio no handler (uma vez por arquivo) — fora de qualquer efeito/StrictMode.
      for (const item of accepted) {
        startUpload(item.id, item.file, classification)
      }
    }
    // As recusas refletem sempre a ultima selecao (substitui, nao acumula).
    setRejections(rejected)
  }

  const openPicker = () => {
    inputRef.current?.click()
  }

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    addFiles(event.target.files)
    // Zera para permitir reenviar o mesmo arquivo (o onChange nao dispara para valor identico).
    event.target.value = ''
  }

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setIsDragging(false)
    addFiles(event.dataTransfer.files)
  }

  const dismissUpload = (id: string) => {
    setUploads((current) => current.filter((item) => item.id !== id))
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('knowledge.upload.title')}</CardTitle>
        <CardDescription>{t('knowledge.upload.subtitle')}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div
          onDragOver={handleDragOver}
          onDragEnter={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={cn(
            'flex flex-col items-center gap-3 rounded-lg border-2 border-dashed px-6 py-10 text-center transition-colors',
            isDragging
              ? 'border-action-primary bg-status-info-subtle'
              : 'border-border-default bg-background-subtle',
          )}
        >
          <UploadCloud aria-hidden="true" className="h-8 w-8 text-foreground-muted" />
          <div className="flex flex-col gap-1">
            <p className="text-sm font-medium text-foreground-primary">
              {t('knowledge.upload.dropzone.instruction')}
            </p>
            <p className="text-xs text-foreground-muted">{t('knowledge.upload.dropzone.hint')}</p>
          </div>
          <input
            ref={inputRef}
            id="knowledge-upload-input"
            type="file"
            multiple
            accept={ACCEPT_ATTR}
            tabIndex={-1}
            className="sr-only"
            aria-label={t('knowledge.upload.dropzone.inputLabel')}
            onChange={handleInputChange}
          />
          <Button type="button" variant="secondary" size="sm" onClick={openPicker}>
            {t('knowledge.upload.dropzone.button')}
          </Button>
        </div>

        <label className="flex items-start gap-2 text-sm text-foreground-primary">
          <input
            type="checkbox"
            checked={isInternal}
            onChange={(event) => {
              setIsInternal(event.target.checked)
            }}
            className="mt-0.5 h-4 w-4 shrink-0 rounded border-border-default accent-action-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring"
          />
          <span className="flex flex-col gap-0.5">
            <span className="font-medium">{t('knowledge.upload.internal.label')}</span>
            <span className="text-xs text-foreground-muted">
              {t('knowledge.upload.internal.hint')}
            </span>
          </span>
        </label>

        {rejections.length > 0 ? (
          <Alert variant="danger" title={t('knowledge.upload.rejected.title')}>
            <ul className="flex list-disc flex-col gap-1 pl-4">
              {rejections.map((rejection) => (
                <li key={rejection.id}>
                  {t(rejectionReasonKey(rejection.reason), { name: rejection.name })}
                </li>
              ))}
            </ul>
          </Alert>
        ) : null}

        {uploads.length > 0 ? (
          <ul className="flex flex-col gap-2" aria-label={t('knowledge.upload.list.title')}>
            {uploads.map((item) => (
              <UploadItem
                key={item.id}
                tenantId={tenantId}
                upload={item}
                onDismiss={() => {
                  dismissUpload(item.id)
                }}
              />
            ))}
          </ul>
        ) : null}
      </CardContent>
    </Card>
  )
}
